import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ElementRef,
    ViewChildren,
    QueryList,
    EventEmitter,
    Output,
    ViewChild,
    AfterViewInit
} from '@angular/core';

import { BehaviorSubject, combineLatest, EMPTY, Subscription } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';

import { DistributionApi } from '../../../../api/distributions/distribution.api';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { AccountModel } from '../../../../Core/Users/models/account.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { EventsOverTimePoint } from '../../../../Common/TargetingShared/model/statisticsReport/events.over.time.api.model';
import { DistributionQuotaInfo } from '../../../../Common/Charts/models/distribution.quota.info';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DataSelectDialogComponent } from '../../../../Common/Dialogs/components/DataSelectDialog/data-select-dialog.component';
import { DataSelectDialogModel } from '../../../../Common/Dialogs/models/data-select-dialog.model';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { JournodexGeographyModel } from '../../../../Common/Journodex/models/journodex-geography.model';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { AuditableModel } from '../../../../Common/Misc/models/auditable.model';
import { AbstractPageBuilderService } from '../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { FilterListProviderServiceInterface } from '../../../../Common/Filter/services/filter-list-provider-service.interface';
import { TargetingSearchLanguageSelectDialogService } from '../../../../Common/TargetingShared/services/targeting-search-language-select-dialog.service';
import { TargetingSearchRegionSelectDialogService } from '../../../../Common/TargetingShared/services/targeting-search-region-select-dialog.service';
import { TargetingPreferencesService } from '../../../../Common/TargetingShared/services/targeting-preferences.service';
import { TargetingGeographyService } from '../../../../Common/TargetingShared/services/targeting-geography-service';
import { ContactInfoTypesEnum } from '../../../../Common/TargetingShared/enum/contact-info-types.enum';
import { FooterModel } from '../../../../Common/TargetingShared/model/footer.model';
import { FooterDisplayElementModel } from '../../../../Common/TargetingShared/model/footer-display-element.model';
import { distributionFooterBlockType } from '../../../../Common/TargetingShared/model/distribution-block-types.model';
import { BuyQuotaDialogComponent } from '../../../../Common/TargetingShared/components/BuyQuotaDialog/buy-quota-dialog.component';
import { HeaderAssessmentMessage } from '../../../../Common/TargetingShared/services/header-editor.service';
import { LanguageModel } from '../../../../Core/Localization/models/language.model';
import { UserAppTargetingPreferencesModel } from '../../../../Core/Users/models/user-app-preferences.model';
import { DiscoverDisplaySettingsModel } from '../../models/display-settings.model';
import {
    DiscoverDistributionsDisplayFilterModel,
    DiscoverSavedSearchesDisplayFilterModel,
    DiscoverTargetedListsDisplayFilterModel
} from '../../../../Account/UserPreferences/models/discover-display-filter.model';
import { deepCompare } from '../../../../Core/utils';
import type { SectionListItems } from '../UserPreferences/user-preferences.component';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { FooterUnsubLanguagesEnum, footerUnsubLanguagesToUnsubString } from '../../../../Common/TargetingShared/enum/footer-unsub-languages.emum';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { gateCall } from '@Core/rxjs-utils';
import { EmailAccountConnectorPrivacyLevelEnum } from '../../../../api/emailAccountConnector/enums/email-account-connector-privacy-level.enum';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';
import { DataSelectTakeoverPanelComponent } from '@Common/Dialogs/components/DataSelectTakeoverPanel/data-select-takeover-panel.component';

const defaultFooterStyles = {
    'alignment': 'center',
    'backgroundColor': '#C7C7C7',
    'bold': false,
    'color': '#000000',
    'fontSize': '12px'
} as any;

@Component({
    selector: 'agpr-discover-user-preferences',
    templateUrl: './discover-user-preferences.component.html',
    styleUrls: ['./discover-user-preferences.component.scss'],
    providers: [
        TargetingSearchRegionSelectDialogService,
        TargetingSearchLanguageSelectDialogService,
    ],
    standalone: false
})
export class DiscoverUserPreferencesComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly contactInfoTypesEnum = ContactInfoTypesEnum;

    @Input() set discoverPreferences(value: UserAppTargetingPreferencesModel) {
        this._discoverPreferences = value;
        this.getFooterAction$.next(null);
    }
    get discoverPreferences(): UserAppTargetingPreferencesModel {
        return this._discoverPreferences;
    }
    private _discoverPreferences: UserAppTargetingPreferencesModel;

    @Output() childErrors = new EventEmitter<ElementRef[]>();
    @Output() checkDirty = new EventEmitter<void>();
    @Output() sectionListItems = new EventEmitter<SectionListItems>();

    subscriptions: Subscription[] = [];
    defaultFooter: FooterModel = new FooterModel();
    originalFooter: FooterModel;
    invalidEmail: boolean = false;
    contactInfoType: ContactInfoTypesEnum;
    countryLabel: string = 'name';
    footerBlock: BlockModel;
    twigData: any = {};
    currentUser: UserModel;
    currentAccount: AccountModel;
    totalUsagePercentage: number = 0;
    quotaInfo: DistributionQuotaInfo;
    quotaOverTime: EventsOverTimePoint[];
    resetDate: Date;

    loadingDistributionStats: boolean = true;

    // Default Filter
    targetingSearches: AuditableModel[] = [];
    targetingLists: AuditableModel[] = [];
    distributions: AuditableModel[] = [];

    defaultSavedSearchesDisplaySettings: DiscoverDisplaySettingsModel;
    defaultTargetedListsDisplaySettings: DiscoverDisplaySettingsModel;
    defaultSentDistributionDisplaySettings: DiscoverDisplaySettingsModel;
    defaultSavedSearchesFilters: DiscoverSavedSearchesDisplayFilterModel;
    defaultTargetedListsFilters: DiscoverTargetedListsDisplayFilterModel;
    defaultDistributionsFilters: DiscoverDistributionsDisplayFilterModel;

    // Default Search

    savedSearchesDefaultSearch = {
        includePrivateContacts : true,
        locations:  new IncludeExcludeModel<number>(),
        languages:  new IncludeExcludeModel<string>()
    };

    distributionDefaultDetails:  {
        fromName: string;
        replyEmail: string;
    };

    defaultRecipients: string[] = [];

    enableMppEstimates: boolean;

    unsubLanguageDropdownOptions: DropdownOptionModel[] = [];
    selectedUnsubLanguageOption: DropdownOptionModel;

    assessmentMessages: HeaderAssessmentMessage = new HeaderAssessmentMessage();

    overlayContainer: Element;

    private childErrorReferences: ElementRef[] = [];
    private afterView = false;
    private getFooterAction$ = new BehaviorSubject<void>(null);
    private isInitializingFooter = false;

    @ViewChild('replyEmailInput', { read: ElementRef }) replyEmailInput: ElementRef;
    @ViewChildren('section') set sections(list: QueryList<ElementRef>) {
        if (list) {
            this.sectionListItems.emit(
                list.toArray().map(item => ({
                    label: `UserPreferences.${item.nativeElement.id}`,
                    value: item
                }))
            );
        }
    };
    @ViewChild('fromNameLiveRegion') fromNameLiveRegion: ElementRef;

    emailAccountOptions: DropdownOptionModel[] = [];
    selectedDefaultEmailAccount: DropdownOptionModel;

    constructor(
        private userPreferencesService: UserPreferencesService,
        public journodexRegionsService: JournodexRegionsService,
        private currentUserStateService: CurrentUserStateService,
        private distributionApi: DistributionApi,
        private targetingPreferencesService: TargetingPreferencesService,
        private coreDialogService: CoreDialogService,
        private regionSelectDialogService: TargetingSearchRegionSelectDialogService,
        private languageSelectDialogService: TargetingSearchLanguageSelectDialogService,
        public localizationService: LocalizationService,
        public targetingGeographyService: TargetingGeographyService,
        public dataConnectionService: DataConnectionService,
        @Inject('TargetingSearchesService') public targetingSearchesService: FilterListProviderServiceInterface,
        @Inject('TargetingListService') public targetingListService: FilterListProviderServiceInterface,
        @Inject('DistributionService') private distributionService: FilterListProviderServiceInterface,
    ) {}

    ngAfterViewInit(): void {
        this.afterView = true;
    }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions.push(
            this.getFooterAction$.pipe(
                tap(() => this.isInitializingFooter = true),
                switchMap(() => {
                    return this.userPreferencesService.getDistributionDefaultFooter().pipe(
                        tap(defaultFooter => {
                            this.defaultFooter = defaultFooter;
                            this.setFooterBlock();
                            if (!this.defaultFooter.senderEmail && !this.defaultFooter.senderPhone) {
                                this.setContactInfoType(ContactInfoTypesEnum.email);
                            } else if (this.defaultFooter.senderEmail) {
                                this.contactInfoType = ContactInfoTypesEnum.email;
                            } else if (this.defaultFooter.senderPhone) {
                                this.contactInfoType = ContactInfoTypesEnum.phone;
                            }
                            this.selectedUnsubLanguageOption =
                                this.unsubLanguageDropdownOptions.find(option => option.value === this.defaultFooter.unsubLinkLanguage);
                            if (!this.selectedUnsubLanguageOption) {
                                this.selectedUnsubLanguageOption = this.unsubLanguageDropdownOptions[0];
                            }
                            this.isInitializingFooter = false;
                        }),
                        catchError(err => {
                            this.isInitializingFooter = false;
                            return EMPTY;
                        })
                    );
                })
            ).subscribe(),
            this.distributionApi.getDefaultFooter().subscribe(footer => this.originalFooter = new FooterModel(footer)),
            this.targetingSearchesService.getFilterListItems().subscribe(
                searches => {
                    if (searches) {
                        this.targetingSearches.length = 0;
                        this.targetingSearches.push(...searches);
                        this.defaultSavedSearchesFilters = new DiscoverSavedSearchesDisplayFilterModel(
                            this.discoverPreferences.savedSearchesPrefs.defaultFilters
                        );
                    }
                }
            ),
            this.targetingListService.getFilterListItems().subscribe(
                lists => {
                    if (lists) {
                        this.targetingLists.length = 0;
                        this.targetingLists.push(...lists);
                        this.defaultTargetedListsFilters = new DiscoverTargetedListsDisplayFilterModel(
                            this.discoverPreferences.targetedListsPrefs.defaultFilters
                        );
                    }
                }
            ),
            this.distributionService.getFilterListItems().subscribe(
                distributions => {
                    if (distributions) {
                        this.distributions.length = 0;
                        this.distributions.push(...distributions);
                        this.defaultDistributionsFilters = new DiscoverDistributionsDisplayFilterModel(
                            this.discoverPreferences.distributionsPrefs.defaultFilters
                        );
                    }
                }
            ),
            this.currentUserStateService.getUser().pipe(filter(x => !!x)).subscribe(user => this.currentUser = user),
            this.currentUserStateService.getAccount().subscribe(account => this.currentAccount = account),
            this.targetingPreferencesService.distributionQuotaInfo$.pipe(filter(x => !!x)).subscribe(quota => {
                this.totalUsagePercentage = (quota.usedDistributions / quota.totalDistributions) * 100;
                this.quotaInfo = quota;
                this.loadingDistributionStats = false;
            }),
            this.targetingPreferencesService.getQuotaExpiryDate().subscribe(resetDate => this.resetDate = resetDate),
            this.distributionApi.getQuotaOverTime().subscribe(events => {
                this.quotaOverTime = events;
            }),
            gateCall(this.dataConnectionService.currentUserHasEmailAccountConnectorAccessOnEnvironment()).pipe(
                switchMap(() => combineLatest([this.dataConnectionService.emailAccountConnectors$, this.currentUserStateService.getUser()]))
            ).subscribe(([connectors, user]) => {
                this.emailAccountOptions = [];
                this.emailAccountOptions.push({
                    label: this.localizationService.transform('Discover.userPreferences.defaultEmailAccount.system'),
                    value: 0
                });
                connectors?.forEach(connector => {
                    if (connector.privacyLevel === EmailAccountConnectorPrivacyLevelEnum.SHARED || connector.userId === user.id) {
                        this.emailAccountOptions.push({
                            label: `${connector.connectorName} (${connector.connectorEmail})`,
                            value: connector.id
                        });
                    }
                });

                if (this._discoverPreferences.distributionsPrefs?.defaultEmailAccount
                    && connectors.some(connector => connector.id === this._discoverPreferences.distributionsPrefs.defaultEmailAccount)) {
                    const index = this.emailAccountOptions.findIndex(x => x.value === this._discoverPreferences.distributionsPrefs.defaultEmailAccount);
                    this.selectedDefaultEmailAccount = this.emailAccountOptions[index];
                } else {
                    this.selectedDefaultEmailAccount = this.emailAccountOptions[0];
                }
            }),
        );

        this.defaultSavedSearchesDisplaySettings = new DiscoverDisplaySettingsModel(this.discoverPreferences.savedSearchesPrefs.defaultDisplaySettings);
        this.defaultTargetedListsDisplaySettings = new DiscoverDisplaySettingsModel(this.discoverPreferences.targetedListsPrefs.defaultDisplaySettings);
        this.defaultSentDistributionDisplaySettings = new DiscoverDisplaySettingsModel(
            this.discoverPreferences.distributionsPrefs.sentDistributionDisplaySettings
        );
        this.distributionDefaultDetails = {
            fromName: this.discoverPreferences.distributionsPrefs.defaultDistributionDetails.fromName,
            replyEmail: this.discoverPreferences.distributionsPrefs.defaultDistributionDetails.replyTo
        };
        this.enableMppEstimates = this.discoverPreferences.distributionsPrefs.enableMppEstimates;

        this.defaultRecipients = this.discoverPreferences.distributionsPrefs.defaultRecipients.slice();

        this.assessFromName();

        this.savedSearchesDefaultSearch = {
            includePrivateContacts: this.discoverPreferences.savedSearchesPrefs.defaultSearchSettings.includePrivateContacts,
            locations: new IncludeExcludeModel<number>(this.discoverPreferences.savedSearchesPrefs.defaultSearchSettings.locations),
            languages: new IncludeExcludeModel<string>(this.discoverPreferences.savedSearchesPrefs.defaultSearchSettings.languages),
        };

        Object.values(FooterUnsubLanguagesEnum).forEach(item => {
            this.unsubLanguageDropdownOptions.push(new DropdownOptionModel(
                this.localizationService.transform(item) + ' - ' + footerUnsubLanguagesToUnsubString.get(item),
                item
            ));
        });
    }

    getUpdatedValues(): UserAppTargetingPreferencesModel {
        return {
            savedSearchesPrefs: {
                defaultFilters: this.defaultSavedSearchesFilters?.getFilterValues() ?? this.discoverPreferences.savedSearchesPrefs.defaultFilters,
                defaultSearchSettings: {
                    includePrivateContacts: this.savedSearchesDefaultSearch.includePrivateContacts,
                    locations: this.savedSearchesDefaultSearch.locations.toApiModel(),
                    languages: this.savedSearchesDefaultSearch.languages.toApiModel()
                },
                defaultDisplaySettings: this.defaultSavedSearchesDisplaySettings.getCurrentSelections()
            },
            targetedListsPrefs: {
                defaultFilters: this.defaultTargetedListsFilters?.getFilterValues() ?? this.discoverPreferences.targetedListsPrefs.defaultFilters,
                defaultDisplaySettings: this.defaultTargetedListsDisplaySettings.getCurrentSelections()
            },
            distributionsPrefs: {
                defaultFilters: this.defaultDistributionsFilters?.getFilterValues() ?? this.discoverPreferences.distributionsPrefs.defaultFilters,
                defaultDistributionDetails: {
                    fromName: this.distributionDefaultDetails.fromName,
                    replyTo: this.distributionDefaultDetails.replyEmail.trim()
                },
                defaultRecipients: this.defaultRecipients,
                sentDistributionDisplaySettings: this.defaultSentDistributionDisplaySettings.getCurrentSelections(),
                enableMppEstimates: this.enableMppEstimates,
                defaultEmailAccount: this.selectedDefaultEmailAccount?.value
                // Default footer is handled separately due to separate endpoint in backend
            },
        };
    }

    /**
     * DISTRIBUTION FOOTER REGION
     */
    setFooterBlock() {
        this.footerBlock = new BlockModel(
            null,
            distributionFooterBlockType,
            new FooterDisplayElementModel(this.defaultFooter, defaultFooterStyles)
        );
        // converting the distributionService which was injected using token to PageBuilder
        const pageBuilderService = this.distributionService as unknown as AbstractPageBuilderService;
        pageBuilderService.hasDefaultTemplates().pipe(
            take(1)
        ).subscribe(() => pageBuilderService.setTemplatesOnBlocks([this.footerBlock]));
        this.footerBlock.canAddOrRemove = false;
        this.footerBlock.canMove = false;
        this.footerBlock.canEdit = false;
        this.footerBlock.canDuplicate = false;
        const data: any = {};
        this.twigData = Object.assign(data, this.footerBlock.displayElement, this.footerBlock.displayElement.settings);
    }

    emitFooterChanges() {
        if (this.footerBlock) {
            const footerDisplayElement = this.footerBlock.displayElement as FooterDisplayElementModel;
            footerDisplayElement.footer = this.defaultFooter;
            footerDisplayElement.updateRenderedText();
            const data: any = {};
            this.twigData = Object.assign(data, this.footerBlock.displayElement, this.footerBlock.displayElement.settings);
            this.updateDirtyFlag();
        }
    }

    setContactInfoType(type: ContactInfoTypesEnum): void {
        if (this.isInitializingFooter && this.contactInfoType === ContactInfoTypesEnum.phone) {
            return;
        }
        this.contactInfoType = type;
        if (this.contactInfoType === ContactInfoTypesEnum.email) {
            this.defaultFooter.senderPhone = null;
            this.defaultFooter.senderEmail = this.currentUser?.email;
        } else {
            this.defaultFooter.senderEmail = null;
        }
        this.emitFooterChanges();
    }

    setRenderedOutput(event) {
        this.footerBlock.renderedOutput = event;
    }

    openBuyQuotaDialog() {
        this.coreDialogService.open(BuyQuotaDialogComponent, {
            inputs: {
                confirmAction: (amount) => {
                    return this.targetingPreferencesService.buyQuota(amount);
                }
            },
            outputs: {}
        });
    }

    showLanguageBrowser(includeOrExclude: IncludeOrExclude) {
        const languageIds = this.savedSearchesDefaultSearch.languages[includeOrExclude];
        this.languageSelectDialogService.prepareData(includeOrExclude, languageIds);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io: DataSelectDialogModel = {
            inputs: {
                id: 'discover.userPreferences.search.languageSelectDialog',
                dialogService: this.languageSelectDialogService,
                dialogHeader: 'Monitoring.result.languages',
                dialogMessage: 'Monitoring.result.selectLanguagesMsg',
                leftColumnHeader: 'Monitoring.result.availableLanguages',
                rightColumnHeader: 'Monitoring.result.selectedLanguages',
                overlayContainer: this.overlayContainer,
                filterDataType: {
                    type: 'Monitoring.result.languages',
                    noResultsLocalizationString: 'Common.forms.treeList.noResultsFeminine'
                },
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedLanguages: LanguageModel[]) => {
                    if (!selectedLanguages) {
                        return;
                    }
                    this.savedSearchesDefaultSearch.languages[includeOrExclude] = selectedLanguages?.map(language => language.id);

                    // HACK: pass by reference.. so the value never gets updated in the agpr-include-exclude-chips
                    this.savedSearchesDefaultSearch.languages = new IncludeExcludeModel<string>(this.savedSearchesDefaultSearch.languages);
                    this.updateDirtyFlag();
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    showRegionBrowser(includeOrExclude: IncludeOrExclude) {
        const regionIds = this.savedSearchesDefaultSearch.locations[includeOrExclude];
        this.regionSelectDialogService.prepareData(includeOrExclude, regionIds);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io: DataSelectDialogModel = {
            inputs: {
                id: 'discover.userPreferences.search.regionSelectDialog',
                dialogService: this.regionSelectDialogService,
                dialogHeader: 'Monitoring.search.regions',
                dialogMessage: 'Monitoring.search.selectedRegionsMsg',
                leftColumnHeader: 'Monitoring.search.availableRegions',
                rightColumnHeader: 'Monitoring.search.selectedRegions',
                lockMessage: 'Discover.dialogs.browsers.lockedGeo',
                overlayContainer: this.overlayContainer,
                animationTrigger
            },
            outputs: {
                dialogClose: (selectedRegions: JournodexGeographyModel[]) => {
                    if (!selectedRegions) {
                        return;
                    }
                    this.savedSearchesDefaultSearch.locations[includeOrExclude] = selectedRegions?.map(region => region.collectionId);

                    // HACK: pass by reference.. so the value never gets updated in the agpr-include-exclude-chips
                    this.savedSearchesDefaultSearch.locations = new IncludeExcludeModel<number>(this.savedSearchesDefaultSearch.locations);
                    this.updateDirtyFlag();
                }
            }
        };
        this.coreDialogService.open(DataSelectTakeoverPanelComponent, io);
    }

    assessFromName() {
        if (!this.fromNameLiveRegion) {
            return;
        }

        const messages: string[] = [];
        (this.fromNameLiveRegion.nativeElement as HTMLElement).textContent = '';
        if (this.distributionDefaultDetails.fromName) {
            if (this.distributionDefaultDetails.fromName.length > 20) {
                messages.push(this.localizationService
                    .transform('Discover.userPreferences.defaultDetails.from.warning.length'));
            }
            if (this.distributionDefaultDetails.fromName.search(/(.+)@(.+){2,}\.(.+){2,}/) > -1) {
                messages.push(this.localizationService
                    .transform('Discover.userPreferences.defaultDetails.from.warning.email'));
            }
            if (messages.length > 0) {
                (this.fromNameLiveRegion.nativeElement as HTMLElement).textContent = this.localizationService.transform('Common.input.showWarningMessage');
            }
        }
        this.assessmentMessages.fromName = messages;
    }

    onEmailChange(emailEvent: { isValid: boolean; elementRef: ElementRef }) {
        if (!emailEvent.isValid) {
            this.childErrorReferences.push(emailEvent.elementRef);
        } else {
            const index = this.childErrorReferences.findIndex(ref => ref === emailEvent.elementRef);
            if (index >= 0) {
                this.childErrorReferences.splice(index, 1);
            }
        }
        this.childErrors.emit(this.childErrorReferences);
        this.updateDirtyFlag();
    }

    updateDirtyFlag() {
        if (!this.afterView) {
            return;
        }
        this.checkDirty.emit();
    }

    isFooterDirty(): boolean {
        // IsEqual does not allow null/undefined/emptystring checks
        return !deepCompare(this.defaultFooter, this.originalFooter, { emptyChecker: 'all' });
    }

    ngOnDestroy(){
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onSelectUnsubLanguage(selectedItem: DropdownOptionModel): void {
        this.defaultFooter.unsubLinkLanguage = selectedItem.value;
        this.emitFooterChanges();
    }
}
